import React, { useId, useState } from "react";
import { strings } from "../../resources/strings";
import {
  resolveLocalizedEntry,
  langAttribute,
} from "../../utils/questionUtils";
import { LocalizedParticipantQuestion, QuestionType } from "../../apis/API";
import BoolContextCard from "./BoolContextCard";
import {
  AnswerIcon,
  CHECK_PATH,
  CLOSE_PATH,
  saveButtonClass,
} from "./workflow/answerButtons";
import FloatingLabelField from "./workflow/FloatingLabelField";

/** Answer payload emitted by a bool question (subset of IdentifiableAnswerV3Input). */
export interface BoolAnswer {
  questionId: string;
  answer: boolean;
  notes?: string;
  questionType: QuestionType.bool;
}

export interface BoolQuestionProps {
  question: LocalizedParticipantQuestion;
  /** Optional prepopulated context shown below the headline. */
  context?: Record<string, string>;
  /** Prior answer to re-display on resume / back navigation (flow engine). */
  defaultAnswer?: boolean | null;
  /** Prior Yes-notes to re-display on resume / back navigation. */
  defaultNotes?: string;
  onAnswer: (answer: BoolAnswer) => void;
}

/**
 * Bool (Yes/No) question component. Renders the API-provided question text, two
 * answer buttons, and — on Yes — a notes field. Presentational; the flow engine
 * (IHA-347) owns sequencing and assembles the full V3 answer payload.
 */
const BoolQuestion: React.FC<BoolQuestionProps> = ({
  question,
  context,
  defaultAnswer,
  defaultNotes,
  onAnswer,
}) => {
  // null = unanswered; true/false = the participant's current selection. Drives
  // both the selected button styling and (on Yes) the notes field. Seeded from
  // a prior answer so the flow engine can re-display on resume / back.
  const [selectedAnswer, setSelectedAnswer] = useState<boolean | null>(
    defaultAnswer ?? null,
  );
  const [notes, setNotes] = useState<string>(defaultNotes ?? "");
  const headlineId = useId();

  const questionId = question.id ?? "";
  const headlineEntry = resolveLocalizedEntry(question.localization);
  const headline = headlineEntry?.text ?? "";
  // SC 3.1.2 — the resolver falls back across languages, so text can arrive in
  // a language the document does not declare. undefined when it matches.
  const headlineLang = langAttribute(headlineEntry?.locale);
  const showNotes = selectedAnswer === true;

  // Bool shows its answer buttons and the primary "Save & Continue" on the SAME
  // screen, so the answer buttons use an outlined-selected style (light fill +
  // emphasized primary border) rather than the filled green — the primary green
  // is reserved for the single Save CTA. (Callie 2026-07-02; Bool-specific — the
  // workflow forms put answers and Save on separate screens, so they keep the
  // shared filled-green `answerButtonClass`.)
  const answerButtonClass = (selected: boolean) =>
    `flex flex-1 items-center justify-center gap-2 min-h-[44px] h-16 rounded-2xl border-2 bg-[#FCFCFC] text-primary text-base font-semibold shadow-[0px_2px_2px_rgba(0,0,0,0.15)] transition-all duration-200 active:scale-95 ${
      selected ? "border-primary" : "border-[#A1B1C8]"
    }`;

  const handleNo = () => {
    setSelectedAnswer(false);
    // Discard any notes typed for a prior Yes so they can't resurface / re-emit.
    setNotes("");
    onAnswer({ questionId, answer: false, questionType: QuestionType.bool });
  };

  const handleYes = () => {
    setSelectedAnswer(true);
  };

  const confirmYes = () => {
    onAnswer({
      questionId,
      answer: true,
      notes: notes.trim(),
      questionType: QuestionType.bool,
    });
  };

  return (
    <div data-testid="bool-question" className="flex flex-col gap-6">
      <h2 id={headlineId} lang={headlineLang} className="text-2xl text-black">
        {headline}
      </h2>

      {context && <BoolContextCard context={context} />}

      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleYes}
          aria-pressed={selectedAnswer === true}
          className={answerButtonClass(selectedAnswer === true)}
        >
          <AnswerIcon testId="answer-icon-yes" path={CHECK_PATH} />
          {strings.questionAnswers.yes}
        </button>
        <button
          type="button"
          onClick={handleNo}
          aria-pressed={selectedAnswer === false}
          className={answerButtonClass(selectedAnswer === false)}
        >
          <AnswerIcon testId="answer-icon-no" path={CLOSE_PATH} />
          {strings.questionAnswers.no}
        </button>
      </div>

      {showNotes && (
        <div className="flex flex-col gap-3">
          <FloatingLabelField
            multiline
            value={notes}
            onChange={setNotes}
            testId="bool-notes"
            label={strings.questionAnswers.yesNotesPlaceholder}
            ariaLabelledBy={headlineId}
          />
          <button
            type="button"
            onClick={confirmYes}
            disabled={notes.trim().length === 0}
            className={saveButtonClass}
          >
            <AnswerIcon testId="save-continue-icon" path={CHECK_PATH} />
            {strings.reportForm.workflow.saveContinue}
          </button>
        </div>
      )}
    </div>
  );
};

export default BoolQuestion;
